import orderBy from 'lodash.orderby';

import type { EmailHistoryQuerySchema } from '@/_pages/settings/email-history/shared/schemas/email-history-query-schema';
import { isFutureScheduledEmailHistory } from '@/_pages/settings/email-history/shared/utils/format-email-history-sent-or-scheduled';
import { dateTime } from '@/_shared';

import { hasParentBillingContact } from './has-parent-billing-contact';
import type { InvoiceEmailDeliveryStatusSchema } from './schemas/invoice-email-delivery-status-schema';
import type { InvoiceLatestEmailHistorySchema } from './schemas/invoices/invoice-query-agreement-link-schema';
import type { InvoiceQuerySchema } from './schemas/invoices/invoice-query-schema';

export type InvoiceEmailBadgeState = 'warning' | 'none' | 'success' | 'failure' | 'pending' | 'scheduled';

type LatestEmailHistorySummary = Pick<
  EmailHistoryQuerySchema | InvoiceLatestEmailHistorySchema,
  'delivery_status' | 'scheduled_at' | 'sent_at'
>;

function mapDeliveryStatusToBadge(status: string | null | undefined): InvoiceEmailBadgeState {
  if (!status) {
    return 'none';
  }

  switch (status.toLowerCase() as InvoiceEmailDeliveryStatusSchema) {
    case 'scheduled':
      return 'pending';
    case 'accepted':
    case 'delivered':
      return 'success';
    case 'pending':
      return 'pending';
    case 'failed':
    case 'quarantined':
      return 'failure';
    default:
      return 'none';
  }
}

function getLatestEmailHistory(invoice: InvoiceQuerySchema): EmailHistoryQuerySchema | null {
  const histories = invoice.email_histories ?? [];
  if (histories.length === 0) {
    return null;
  }

  const sorted = orderBy(
    histories,
    [(history) => dateTime.parse(history.sent_at ?? history.scheduled_at ?? history.created_at).valueOf()],
    ['desc']
  );

  return sorted[0] ?? null;
}

function resolveLatestEmailHistory(invoice: InvoiceQuerySchema): LatestEmailHistorySummary | null {
  return invoice.latest_email_history ?? getLatestEmailHistory(invoice);
}

export function deriveInvoiceEmailBadge(invoice: InvoiceQuerySchema): InvoiceEmailBadgeState {
  if (!hasParentBillingContact(invoice)) {
    return 'warning';
  }

  const latest = resolveLatestEmailHistory(invoice);
  if (latest && isFutureScheduledEmailHistory(latest)) {
    return 'scheduled';
  }

  return mapDeliveryStatusToBadge(latest?.delivery_status);
}

export function getLatestEmailHistoryFromInvoice(invoice: InvoiceQuerySchema): EmailHistoryQuerySchema | null {
  return getLatestEmailHistory(invoice);
}
